import { Link } from "react-router-dom";
import { Star, Cookie } from "lucide-react";
import { useTracking } from "@/contexts/TrackingContext";

const horseLogo = "/assets/localbest_transparent_v2.png";

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { showConsentSettings } = useTracking();

  const footerLinks = {
    directory: [
      { name: "All Categories", path: "/directory" },
      { name: "Featured Businesses", path: "/directory?featured=true" },
    ],
    business: [
      { name: "Create My Free Listing", path: "/submit" },
      { name: "Dashboard Login", path: "/auth/login" },
      { name: "Get $120K Funding", path: "/get-funded" },
      { name: "TV Ads Platform", path: "/tv-ads-platform" },
      { name: "Postcard That Talks Back™", path: "/postcard-that-talks-back" },
      { name: "Path to Funding", href: "https://aihpower.com/best-of-best-listing" },
      { name: "Get AI Horse-Powered", href: "https://aihpower.com" },
    ],
    onlybest: [
      { name: "OnlyBest Movement", path: "/onlybest" },
      { name: "Leaderboard", path: "/leaderboard" },
      { name: "Meet the Creators", path: "/creators" },
      { name: "Become a Creator", path: "/submit?kind=creator" },
      { name: "Start an Affiliate Program", path: "/submit" },
      { name: "Creator Disclosure", path: "/onlybest#disclosure" },
    ],
    company: [
      { name: "About Us", href: "https://localbest.ai" },
      { name: "Contact", href: "mailto:support@localbest.ai" },
      { name: "Privacy Policy", path: "/privacy" },
      { name: "Terms of Service", path: "/terms" },
      { name: "AI User Policy", path: "/ai-policy" },
    ],
  };

  return (
    <footer className="bg-[#020817] border-t border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center mb-6" data-testid="footer-logo-link">
              <img 
                src={horseLogo} 
                alt="LocalBest.ai" 
                className="h-14 w-auto object-contain"
              />
            </Link>
            <p className="text-[#94A3B8] text-sm leading-relaxed mb-6 max-w-sm">
              Discover the world's top businesses powered by AI Agents. Connect,
              chat, and find the best services for your needs.
            </p>
          </div>

          {/* Directory Links */}
          <div>
            <h4 className="text-white font-semibold mb-4 font-['Outfit']">
              Directory
            </h4>
            <ul className="space-y-3">
              {footerLinks.directory.map((link) => (
                <li key={link.path + link.name}>
                  <Link
                    to={link.path}
                    className="text-[#94A3B8] text-sm hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Business Links */}
          <div>
            <h4 className="text-white font-semibold mb-4 font-['Outfit']">
              For Business
            </h4>
            <ul className="space-y-3">
              {footerLinks.business.map((link) => (
                <li key={(link.path || link.href) + link.name}>
                  {link.href ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#94A3B8] text-sm hover:text-white transition-colors"
                    >
                      {link.name}
                    </a>
                  ) : (
                    <Link
                      to={link.path}
                      className="text-[#94A3B8] text-sm hover:text-white transition-colors"
                    >
                      {link.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* OnlyBest Links */}
          <div>
            <h4 className="text-white font-semibold mb-4 font-['Outfit'] flex items-center gap-1.5">
              <span>OnlyBest™</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#FBBF24]/15 text-[#FBBF24] border border-[#FBBF24]/30">NEW</span>
            </h4>
            <ul className="space-y-3">
              {footerLinks.onlybest.map((link) => (
                <li key={link.path + link.name}>
                  <Link
                    to={link.path}
                    className="text-[#94A3B8] text-sm hover:text-[#FBBF24] transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="text-white font-semibold mb-4 font-['Outfit']">
              Company
            </h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.path + link.name}>
                  <Link
                    to={link.path}
                    className="text-[#94A3B8] text-sm hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
              {/* Admin Login */}
              <li>
                <Link
                  to="/admin"
                  className="text-[#94A3B8] text-sm hover:text-white transition-colors flex items-center gap-1"
                  data-testid="admin-login-link"
                >
                  Admin Login
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-[#64748B] text-sm">
            © {currentYear} LocalBest.ai — The AI Horse-Powered Best of Best Directory™. All rights reserved.
          </p>
          <div className="flex items-center gap-4 flex-wrap">
            <button
              type="button"
              onClick={showConsentSettings}
              className="text-[#64748B] text-sm hover:text-[#94A3B8] transition-colors inline-flex items-center gap-1.5"
              data-testid="footer-cookie-settings"
              aria-label="Your Privacy Choices"
            >
              <Cookie className="w-3.5 h-3.5" /> Your Privacy Choices
            </button>
            <Link to="/auth/login" className="text-[#64748B] text-sm hover:text-[#94A3B8] transition-colors" data-testid="footer-dashboard-login">
              Dashboard
            </Link>
            <Link to="/admin" className="text-[#64748B] text-sm hover:text-[#94A3B8] transition-colors">
              Admin
            </Link>
            <a href="https://localbest.ai" target="_blank" rel="noopener noreferrer" className="text-[#64748B] text-sm hover:text-[#94A3B8] transition-colors">
              localbest.ai
            </a>
            <p className="text-[#64748B] text-sm flex items-center gap-2">
              Powered by AI
              <span className="inline-block w-2 h-2 rounded-full bg-[#00E5FF] animate-pulse" />
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
